import React, { useState, useEffect, Suspense } from 'react';
import { AppMode, User } from './types';
import Layout from './components/Layout';
import LandingPage from './components/LandingPage';
import Auth from './components/Auth';
import ErrorBoundary from './components/ErrorBoundary';
import { PWAInstallPrompt } from './components/PWAInstallPrompt';
import { PWAUpdateBanner } from './components/PWAUpdateBanner';
import { OfflineIndicator } from './components/OfflineIndicator';
import { initializePWA } from './services/pwa';
import { ThemeProvider } from './contexts/ThemeContext';
const Dashboard = React.lazy(() => import('./components/Dashboard'));
const SubjectChat = React.lazy(() => import('./components/SubjectChat'));
const VideoAnalyzer = React.lazy(() => import('./components/VideoAnalyzer'));
const ActiveRecall = React.lazy(() => import('./components/ActiveRecall'));
const AiMarker = React.lazy(() => import('./components/AiMarker'));
const PaperGuide = React.lazy(() => import('./components/PaperGuide'));
const InteractiveWhiteboard = React.lazy(() => import('./components/InteractiveWhiteboard'));
const QuickLearn = React.lazy(() => import('./components/QuickLearn'));
const Flashcards = React.lazy(() => import('./components/Flashcards'));
const Roadmap = React.lazy(() => import('./components/Roadmap'));
const Notes = React.lazy(() => import('./components/Notes'));
const MultipleChoice = React.lazy(() => import('./components/MultipleChoice'));
const Settings = React.lazy(() => import('./components/Settings'));
const AdminDashboard = React.lazy(() => import('./components/AdminDashboard'));
const OnboardingTour = React.lazy(() => import('./components/OnboardingTour'));
import { getCurrentUser, setCurrentUser, getGoogleSessionIdFromUrl, processGoogleCallback, setupOAuthDeepLinkListener } from './services/auth';
import { isAdminUser } from './services/admin';
import { onNavigate } from './services/navigation';
import { initializeCapacitor, isNativeApp, setStatusBarStyle } from './services/capacitor';

const App: React.FC = () => {
  const [mode, setMode] = useState<AppMode>(AppMode.Landing);
  const [user, setUser] = useState<User | null>(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isProcessingOAuth, setIsProcessingOAuth] = useState(false);
  const [authView, setAuthView] = useState<'sign-in' | 'sign-up'>('sign-in');

  // Initialize Capacitor on app startup (for native mobile apps)
  useEffect(() => {
    initializeCapacitor();
  }, []);

  // Initialize PWA features (service worker, push notifications, etc.)
  useEffect(() => {
    // Only initialize PWA on web, not in Capacitor
    if (!isNativeApp()) {
      initializePWA().catch(console.error);
    }
  }, []);

  // Set up OAuth deep link listener for native apps
  useEffect(() => {
    const handleOAuthSessionId = async (sessionId: string) => {
      setIsProcessingOAuth(true);
      try {
        const loggedInUser = await processGoogleCallback(sessionId);
        setUser(loggedInUser);
        setMode(isAdminUser(loggedInUser) ? AppMode.Admin : AppMode.Dashboard);

        const hasSeenTour = localStorage.getItem(`absorbit_onboarding_${loggedInUser.id}`);
        if (!hasSeenTour) {
          setTimeout(() => setShowOnboarding(true), 500);
        }
      } catch (error) {
        console.error('Google OAuth error:', error);
        setMode(AppMode.Auth);
      } finally {
        setIsProcessingOAuth(false);
      }
    };

    // Set up deep link listener for native apps
    const cleanup = setupOAuthDeepLinkListener(handleOAuthSessionId);
    return cleanup;
  }, []);

  useEffect(() => {
    // Check for Google OAuth callback first (session_id in URL hash)
    const sessionId = getGoogleSessionIdFromUrl();
    if (sessionId) {
      setIsProcessingOAuth(true);
      // Clear the URL hash immediately to prevent re-processing
      window.history.replaceState({}, '', window.location.pathname);
      
      // Process the OAuth callback
      processGoogleCallback(sessionId)
        .then((loggedInUser) => {
          setUser(loggedInUser);
          setMode(isAdminUser(loggedInUser) ? AppMode.Admin : AppMode.Dashboard);
          
          const hasSeenTour = localStorage.getItem(`absorbit_onboarding_${loggedInUser.id}`);
          if (!hasSeenTour) {
            setTimeout(() => setShowOnboarding(true), 500);
          }
        })
        .catch((error) => {
          console.error('Google OAuth error:', error);
          setMode(AppMode.Auth);
        })
        .finally(() => {
          setIsProcessingOAuth(false);
        });
      return;
    }
    
    // Check for existing user session
    const currentUser = getCurrentUser();
    if (currentUser) {
      setUser(currentUser);
      
      const params = new URLSearchParams(window.location.search);
      if (params.get('join_id') || params.get('invite_token') || params.get('share_token')) {
          setMode(AppMode.Notes);
      } else if (isAdminUser(currentUser)) {
          setMode(AppMode.Admin);
      } else {
          setMode(AppMode.Dashboard);
      }

      const hasSeenTour = localStorage.getItem(`absorbit_onboarding_${currentUser.id}`);
      if (!hasSeenTour) {
        setTimeout(() => setShowOnboarding(true), 800);
      }
    }
  }, []);

  useEffect(() => onNavigate((nextMode) => setMode(nextMode)), []);

  // Apply theme based on user preference
  useEffect(() => {
    const applyTheme = (theme: 'light' | 'dark' | undefined) => {
      const isDark = theme === 'dark';
      if (isDark) {
        document.body.classList.remove('theme-day');
      } else {
        // Default to light mode (theme-day)
        document.body.classList.add('theme-day');
      }
      // Update native status bar to match theme
      setStatusBarStyle(isDark);
    };

    applyTheme(user?.theme);
  }, [user, user?.theme]);

  const handleLogin = (loggedInUser: User) => {
    setUser(loggedInUser);
    setCurrentUser(loggedInUser);
    
    const params = new URLSearchParams(window.location.search);
    if (params.get('join_id') || params.get('invite_token') || params.get('share_token')) {
        setMode(AppMode.Notes);
    } else if (isAdminUser(loggedInUser)) {
        setMode(AppMode.Admin);
    } else {
        setMode(AppMode.Dashboard);
    }

    const hasSeenTour = localStorage.getItem(`absorbit_onboarding_${loggedInUser.id}`);
    if (!hasSeenTour) {
      setTimeout(() => setShowOnboarding(true), 500);
    }
  };
  
  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    if (user) {
      localStorage.setItem(`absorbit_onboarding_${user.id}`, 'completed');
    }
  };

  const handleLogout = () => {
    setUser(null);
    setMode(AppMode.Landing);
    setCurrentUser(null); // Clear session
  };

  const handleUpdateUser = (updatedUser: User) => {
      setUser(updatedUser);
      // setCurrentUser is already called in updateUserProfile, but good to be safe if state drifts
      setCurrentUser(updatedUser); 
  };

  const forceCloseSidebar = () => {
      setIsSidebarCollapsed(true);
  };

  const renderContent = () => {
    // Show loading state while processing Google OAuth
    if (isProcessingOAuth) {
      return (
        <div className="flex flex-col items-center justify-center h-screen bg-slate-950">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-12 h-12 border-4 border-cobalt-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-slate-400 text-lg">Signing in with Google...</p>
          </div>
        </div>
      );
    }
    
    switch (mode) {
      case AppMode.Landing:
        return (
          <LandingPage
            onSignIn={() => {
              setAuthView('sign-in');
              setMode(AppMode.Auth);
            }}
            onSignUp={() => {
              setAuthView('sign-up');
              setMode(AppMode.Auth);
            }}
          />
        );
      case AppMode.Auth:
        return <Auth onLogin={handleLogin} initialMode={authView} />;
      case AppMode.Dashboard:
        return <Dashboard user={user} />;
      case AppMode.Roadmap:
        return <Roadmap />;
      case AppMode.Chat:
        return <SubjectChat user={user} />;
      case AppMode.Video:
        return <VideoAnalyzer />;
      case AppMode.ActiveRecall:
        return <ActiveRecall />;
      case AppMode.Flashcards:
        return <Flashcards onStartSession={forceCloseSidebar} />;
      case AppMode.AiMarker:
        return <AiMarker />;
      case AppMode.PaperGuide:
        return <PaperGuide onStartSession={forceCloseSidebar} />;
      case AppMode.InteractiveWhiteboard:
        return <InteractiveWhiteboard onStartSession={forceCloseSidebar} user={user} />;
      case AppMode.QuickLearn:
        return <QuickLearn onStartSession={forceCloseSidebar} />;
      case AppMode.Notes:
        return <Notes onStartSession={forceCloseSidebar} user={user} />;
      case AppMode.MCQ:
        return <MultipleChoice />;
      case AppMode.Focus:
        // Focus mode now redirects to Active Recall with focus target preset
        return <ActiveRecall />;
      case AppMode.Settings:
        return user ? <Settings user={user} onUpdate={handleUpdateUser} /> : null;
      case AppMode.Admin:
        return <AdminDashboard user={user} />;
      default:
        return (
          <LandingPage
            onSignIn={() => {
              setAuthView('sign-in');
              setMode(AppMode.Auth);
            }}
            onSignUp={() => {
              setAuthView('sign-up');
              setMode(AppMode.Auth);
            }}
          />
        );
    }
  };

  return (
    <ThemeProvider>
      <ErrorBoundary>
        <Layout
          currentMode={mode}
          setMode={setMode}
          user={user}
          onLogout={handleLogout}
          isSidebarCollapsed={isSidebarCollapsed}
          toggleSidebar={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        >
          <ErrorBoundary>
            <Suspense
              fallback={
                <div className="flex h-[100dvh] items-center justify-center text-slate-600">
                  Loading Absorbit...
                </div>
              }
            >
              {renderContent()}
            </Suspense>
          </ErrorBoundary>
        </Layout>
        {user && (
          <Suspense fallback={null}>
            <OnboardingTour run={showOnboarding} onComplete={handleOnboardingComplete} />
          </Suspense>
        )}

        {/* PWA Components - only show on web, not in Capacitor */}
        {!isNativeApp() && (
          <>
            <PWAInstallPrompt />
            <PWAUpdateBanner />
            <OfflineIndicator />
          </>
        )}
      </ErrorBoundary>
    </ThemeProvider>
  );
};

export default App;
